import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import { Check, Coins, Zap, Loader2 } from "lucide-react";

const HIGHLIGHTS = {
  starter: ["200 crédits / mois", "Formats 16:9 · 9:16 · 1:1", "Audio IA", "Galerie de projet"],
  pro: ["500 crédits / mois", "Priorité de rendu", "Tous les formats & audio", "Support prioritaire"],
  premium: ["1200 crédits / mois", "Rendu le plus rapide", "Accès anticipé aux nouveautés", "Support dédié"],
};

export default function Pricing() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/paypal/config").then(({ data }) => setConfig(data));
  }, []);

  const onApprove = async (subscriptionID, tier) => {
    try {
      const { data } = await api.post("/paypal/subscription/activate", {
        subscription_id: subscriptionID, tier,
      });
      await refresh();
      toast.success(`Abonnement ${data.status === "ACTIVE" ? "activé" : "enregistré"} !`);
      navigate("/subscription");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!config) {
    return (
      <div className="studio-bg grain min-h-screen">
        <Navbar />
        <div className="grid place-items-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#3ECF8E]" /></div>
      </div>
    );
  }

  const paypalOptions = {
    clientId: config.client_id,
    vault: true,
    intent: "subscription",
    currency: config.currency || "EUR",
  };

  return (
    <div className="studio-bg grain min-h-screen relative">
      <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto fade-up">
          <span className="text-xs font-semibold tracking-wider uppercase text-green-300">Abonnements PayPal</span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">Choisissez votre plan</h1>
          <p className="text-slate-400 mt-4">Facturation mensuelle en euros via PayPal. Annulez à tout moment depuis "Mon abonnement".</p>
        </div>

        {!config.enabled ? (
          <div className="glass rounded-2xl p-10 text-center mt-12 text-slate-400">
            PayPal n'est pas encore configuré. Ajoutez les identifiants PayPal côté serveur.
          </div>
        ) : (
          <PayPalScriptProvider options={paypalOptions}>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {config.tiers.map((p, i) => {
                const featured = p.tier === "pro";
                return (
                  <div key={p.tier}
                    className={`relative glass rounded-3xl p-7 fade-up ${featured ? "border-green-500/60 ring-1 ring-green-500/40" : ""}`}
                    style={{ animationDelay: `${i * 0.08}s` }}
                    data-testid={`plan-card-${p.tier}`}>
                    {featured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold bg-green-600 text-white rounded-full px-3 py-1 flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Populaire
                      </span>
                    )}
                    <h3 className="font-display text-xl font-bold">{p.name}</h3>
                    <div className="mt-3 flex items-end gap-1">
                      <span className="font-display text-4xl font-extrabold">{p.price} €</span>
                      <span className="text-slate-500 mb-1">/mois</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 text-lime-300">
                      <Coins className="w-4 h-4" /><span className="font-mono text-sm">{p.credits} crédits / mois</span>
                    </div>

                    <ul className="mt-6 space-y-3">
                      {(HIGHLIGHTS[p.tier] || []).map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                          <Check className="w-4 h-4 text-[#3ECF8E] mt-0.5 shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-7" data-testid={`paypal-button-${p.tier}`}>
                      {user ? (
                        <PayPalButtons
                          style={{ layout: "vertical", color: "gold", shape: "pill", label: "subscribe" }}
                          createSubscription={(data, actions) => actions.subscription.create({ plan_id: p.plan_id })}
                          onApprove={(data) => onApprove(data.subscriptionID, p.tier)}
                          onError={() => toast.error("Erreur PayPal. Réessayez.")}
                        />
                      ) : (
                        <button onClick={() => navigate("/login")}
                          className="w-full h-11 rounded-full bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn font-semibold text-white"
                          data-testid={`login-to-subscribe-${p.tier}`}>
                          Connectez-vous pour vous abonner
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </PayPalScriptProvider>
        )}

        <p className="text-center text-xs text-slate-500 mt-10 font-mono">
          Paiement sécurisé par PayPal (Sandbox) · Aucune donnée bancaire n'est stockée
        </p>
      </main>
    </div>
  );
}
